import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Upload, 
  CheckSquare, 
  BookOpen, 
  FileText, 
  BarChart3,
  Building2,
  Zap,
  Receipt,
  Users,
  Shield,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { cn } from '../../lib/utils';

const navSections = [
  {
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ],
  },
  {
    heading: 'Processing',
    items: [
      { path: '/upload', icon: Upload, label: 'Upload' },
      { path: '/transactions', icon: CheckSquare, label: 'Review' },
    ],
  },
  {
    heading: 'Accounting',
    items: [
      { path: '/journal', icon: BookOpen, label: 'Journal' },
      { path: '/accounts', icon: FileText, label: 'Accounts' },
    ],
  },
  {
    heading: 'Billing',
    items: [
      { path: '/invoices', icon: Receipt, label: 'Invoices' },
      { path: '/customers', icon: Users, label: 'Customers' },
    ],
  },
  {
    heading: 'Analysis',
    items: [
      { path: '/reports', icon: BarChart3, label: 'Reporting' },
    ],
  },
];

export default function Sidebar() {
  const { user } = useAuth();
  const { credits, tier, org } = useOrg();
  const location = useLocation();

  const tierLabel = tier === 'starter' ? 'Starter' : tier === 'pro' ? 'Pro' : tier === 'business' ? 'Business' : tier === 'enterprise' ? 'Enterprise' : 'Free';
  const monthlyAlloc = credits?.monthly_allocation || 0;
  const monthlyUsed = credits?.monthly_used || 0;
  const usagePercent = monthlyAlloc > 0 ? Math.min(100, Math.round((monthlyUsed / monthlyAlloc) * 100)) : 0;
  const remaining = credits?.total_available || 0;

  return (
    <aside className="w-64 border-r border-zinc-200 bg-white hidden md:flex flex-col h-screen sticky top-0">
      {/* Logo + Company */}
      <div className="px-6 py-4 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#D94E18] rounded-md flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold text-lg text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            kaya.ai
          </span>
        </div>
        {org?.company_name && (
          <p className="mt-2 text-xs font-semibold text-zinc-500 truncate pl-0.5" data-testid="sidebar-company-name" title={org.company_name}>
            {org.company_name}
          </p>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-3 overflow-y-auto">
        {navSections.map((section) => (
          <div key={section.heading || 'main'} className={section.heading ? 'mt-4' : ''}>
            {section.heading && (
              <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                {section.heading}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                               (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        'nav-item',
                        isActive && 'active'
                      )}
                    >
                      <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                      <span className="text-[13px] font-medium">{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Platform Owner — only visible to platform admins */}
        {user?.is_platform_admin && (
          <div className="mt-4">
            <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              Kaya Platform
            </p>
            <ul className="space-y-0.5">
              <li>
                <NavLink
                  to="/platform-admin"
                  data-testid="nav-platform-admin"
                  className={cn(
                    'nav-item',
                    location.pathname === '/platform-admin' && 'active'
                  )}
                >
                  <Shield className="w-[18px] h-[18px]" strokeWidth={1.5} />
                  <span className="text-[13px] font-medium">Kaya Platform</span>
                </NavLink>
              </li>
              {user?.email === 'paul.antunes@inopsia.ai' && (
                <li>
                  <NavLink
                    to="/gtm-admin"
                    data-testid="nav-gtm-admin"
                    className={cn(
                      'nav-item',
                      location.pathname === '/gtm-admin' && 'active'
                    )}
                  >
                    <TrendingUp className="w-[18px] h-[18px]" strokeWidth={1.5} />
                    <span className="text-[13px] font-medium">Growth Engine</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        )}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-zinc-200 p-3">
        {/* Credit meter */}
        {credits && (
          <div className="px-3 py-2.5 bg-zinc-50 rounded-lg" data-testid="credit-meter">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#D94E18]" />
                <span className="text-xs font-semibold text-zinc-700">Credits</span>
              </div>
              {credits.dev_mode ? (
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-testid="dev-mode-badge">DEV</span>
              ) : (
                <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">{tierLabel}</span>
              )}
            </div>
            <div className="w-full h-1.5 bg-zinc-200 rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  usagePercent >= 100 ? "bg-red-500" : usagePercent >= 80 ? "bg-amber-500" : "bg-[#D94E18]"
                )}
                style={{ width: `${usagePercent}%` }}
                data-testid="credit-usage-bar"
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-zinc-400">{monthlyUsed} / {monthlyAlloc} used</span>
              <span className="text-[10px] text-zinc-500 font-medium">{remaining} left</span>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
